import React, { useState, useCallback } from "react";
import debounce from "lodash/debounce";
import { Link, useNavigate } from "react-router-dom";
import "./Signup.css";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false);

  const navigate = useNavigate();
  const BACK_API_BASE_URL = process.env.REACT_APP_BACK_API_BASE_URL;

  const [errors, setErrors] = useState({
    email: false,
    emailFormat: false,
    userName: false,
    password: false,
    confirmPassword: false,
    confirmPasswordMismatch: false,
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const debouncedCheckEmailDuplicated = useCallback(
    debounce(async (email) => {
      const payload = { email: email };
      try {
        const response = await fetch(
          `${BACK_API_BASE_URL}/api/user/emailDuplicated`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result === true) {
            setErrors((prevErrors) => ({ ...prevErrors, email: true }));
            setIsEmailChecked(false);
          } else {
            setIsEmailChecked(true);
            setErrors((prevErrors) => ({ ...prevErrors, email: false }));
          }
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, email: true }));
          setIsEmailChecked(false);
        }
      } catch (error) {
        console.error("이메일 중복 확인 오류 발생:", error);
        setErrors((prevErrors) => ({ ...prevErrors, email: true }));
        setIsEmailChecked(false);
      }
    }, 300),
    []
  );

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailChecked(false);
    if (emailValue === "") {
      // 이메일 입력 필드가 비어있을 때 에러를 초기화
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: false,
        emailFormat: false,
      }));
    } else if (!emailRegex.test(emailValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, emailFormat: true }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, emailFormat: false }));
      debouncedCheckEmailDuplicated(emailValue); // 디바운스된 함수 호출
    }
  };

  const validatePassword = async () => {
    try {
      const response = await fetch(
        `${BACK_API_BASE_URL}/api/user/passwordValidated`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(password),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result === true) {
          setErrors((prevErrors) => ({ ...prevErrors, password: false }));
          return true;
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, password: true }));
          return false;
        }
      } else {
        const errorData = await response.json();
        setErrors((prevErrors) => ({ ...prevErrors, password: true }));
        return false;
      }
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, password: true }));
      return false;
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPasswordMismatch: true,
      }));
      return;
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPasswordMismatch: false,
      }));
    }

    const payload = {
      email: email,
      password: password,
      phoneNumber: phoneNumber,
      userName: userName,
    };

    let header = {};
    header["Content-Type"] = "application/json";
    if (sessionStorage.getItem("token")) {
      header["Authorization"] = sessionStorage.getItem("token");
    }
    try {
      const response = await fetch(`${BACK_API_BASE_URL}/api/user/register`, {
        method: "POST",
        headers: header,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.status === 201) {
        navigate("/login");
      } else {
        alert(`회원가입 실패`);
      }
    } catch (error) {
      console.error("오류 발생:", error);
    }
  };

  const handleEmailVerification = async () => {
    if (!termsAccepted) {
      alert("이용약관에 동의해야 합니다.");
      return;
    }
    try {
      setIsCodeSent(true);
      alert(
        "이메일 인증 코드를 발송 하였습니다.\n최대 1분 정도 소요될 수 있으니 잠시만 기다려주세요."
      );
      const response = await fetch(`${BACK_API_BASE_URL}/api/auth/sendCode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email }),
      });

      if (response.ok) {
      } else {
        const errorData = await response.json();
        alert(`인증 코드 발송 실패: ${errorData.message}`);
      }
    } catch (error) {
      console.error("이메일 인증 중 오류 발생:", error);
      alert("이메일 인증 중 오류가 발생했습니다.");
    }
  };

  const handleVerificationCodeCheck = async () => {
    try {
      const payload = {
        email: email,
        code: verificationCode,
      };

      const response = await fetch(`${BACK_API_BASE_URL}/api/auth/checkCode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        if (result === true) {
          setIsVerified(true);
          alert("인증이 완료되었습니다.");
        } else {
          alert("인증번호가 올바르지 않습니다.");
        }
      } else {
        const errorData = await response.json();
        alert(`인증 실패: ${errorData.message}`);
      }
    } catch (error) {
      console.error("인증 확인 중 오류 발생:", error);
      alert("인증 확인 중 오류가 발생했습니다.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(
      (prevShowConfirmPassword) => !prevShowConfirmPassword
    );
  };

  const handlePhoneNumber = (e) => {
    let inputValue = e.target.value;

    inputValue = inputValue.replace(/[^0-9]/g, "");

    if (inputValue.length > 11) {
      inputValue = inputValue.slice(0, 11);
    }

    if (inputValue.length > 3) {
      inputValue = inputValue.slice(0, 3) + "-" + inputValue.slice(3);
    }

    if (inputValue.length > 8) {
      inputValue = inputValue.slice(0, 8) + "-" + inputValue.slice(8);
    }

    setPhoneNumber(inputValue);
  };

  return (
    <div className="signup-page">
      <div className="signup-top">회원가입</div>
      <div className="signup-container">
        <form className="signup-form">
          {/* 1단계: 이름과 이메일 입력 */}
          {!isCodeSent && (
            <>
              <div className="signup-name-field">
                <div className="signup-label">이름</div>
                <input
                  type="text"
                  id="name"
                  value={userName}
                  placeholder="이름을 입력해 주세요."
                  onChange={(e) => setUserName(e.target.value)}
                  className={errors.userName ? "input-error" : ""}
                />
              </div>

              <div className="signup-number-field">
                <div className="signup-label">휴대폰번호</div>
                <input
                  type="text"
                  id="phone-number"
                  value={phoneNumber}
                  placeholder="010-0000-0000"
                  onChange={(e) => handlePhoneNumber(e)}
                />
              </div>

              <div className="signup-email-field">
                <div className="signup-label">이메일</div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  placeholder="이메일 주소를 입력해 주세요"
                  onChange={handleEmailChange}
                  className={
                    errors.email || errors.emailFormat ? "input-error" : ""
                  }
                />
                {errors.emailFormat && (
                  <div className="signup-error-message">
                    이메일 형식이 올바르지 않습니다.
                  </div>
                )}
                {errors.email && (
                  <div className="signup-error-message">
                    이미 사용 중인 이메일입니다.
                  </div>
                )}
              </div>
              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    termsAccepted ? "checked" : ""
                  }`}
                  onClick={() => setTermsAccepted(!termsAccepted)}
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/72c9e3515179406c891ffd983f931e3a?pvs=4" // 이용약관 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    이용약관
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    privacyPolicyAccepted ? "checked" : ""
                  }`}
                  onClick={() =>
                    setPrivacyPolicyAccepted(!privacyPolicyAccepted)
                  }
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/1cee08d37b584729a45645b6fa71138c?pvs=4" // 개인정보 처리방침 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    개인정보 수집 및 이용
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <button
                type="button"
                className="signup-submit-button"
                onClick={handleEmailVerification}
                disabled={
                  !isEmailChecked ||
                  errors.email ||
                  errors.emailFormat ||
                  email === "" ||
                  !termsAccepted ||
                  !privacyPolicyAccepted
                }
              >
                이메일 인증하기
              </button>
            </>
          )}

          {/* 2단계: 인증번호 입력 */}
          {isCodeSent && !isVerified && (
            <>
              <div className="signup-name-field">
                <div className="signup-label">이름</div>
                <input
                  type="text"
                  id="name"
                  value={userName}
                  placeholder="이름을 입력해 주세요."
                  onChange={(e) => setUserName(e.target.value)}
                  className={errors.userName ? "input-error" : ""}
                />
              </div>
              <div className="signup-email-field">
                <div className="signup-label">이메일</div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  placeholder="이메일 주소를 입력해 주세요"
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "input-error" : ""}
                  disabled
                />
              </div>

              <div className="signup-number-field">
                <div className="signup-label">휴대폰번호</div>
                <input
                  type="text"
                  id="phone-number"
                  value={phoneNumber}
                  placeholder="010-0000-0000"
                  onChange={(e) => handlePhoneNumber(e)}
                />
              </div>

              <div className="signup-verification-field">
                <div className="signup-label">인증번호</div>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  placeholder="인증번호를 입력해 주세요"
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="resend-verification-button"
                onClick={handleEmailVerification}
              >
                이메일 인증 재전송
              </button>
              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    termsAccepted ? "checked" : ""
                  }`}
                  onClick={() => setTermsAccepted(!termsAccepted)}
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/72c9e3515179406c891ffd983f931e3a?pvs=4" // 이용약관 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    이용약관
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    privacyPolicyAccepted ? "checked" : ""
                  }`}
                  onClick={() =>
                    setPrivacyPolicyAccepted(!privacyPolicyAccepted)
                  }
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/1cee08d37b584729a45645b6fa71138c?pvs=4" // 개인정보 처리방침 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    개인정보 수집 및 이용
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <button
                type="button"
                className="signup-submit-button"
                onClick={handleVerificationCodeCheck}
              >
                인증번호 확인
              </button>
            </>
          )}

          {/* 3단계: 비밀번호 입력 */}
          {isVerified && (
            <>
              <div className="signup-name-field">
                <div className="signup-label">이름</div>
                <input
                  type="text"
                  id="name"
                  value={userName}
                  placeholder="이름을 입력해 주세요."
                  onChange={(e) => setUserName(e.target.value)}
                  className={errors.userName ? "input-error" : ""}
                />
              </div>
              <div className="signup-email-field">
                <div className="signup-label">이메일</div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  placeholder="이메일 주소를 입력해 주세요"
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "input-error" : ""}
                  disabled
                />
              </div>

              <div className="signup-number-field">
                <div className="signup-label">휴대폰번호</div>
                <input
                  type="text"
                  id="phone-number"
                  value={phoneNumber}
                  placeholder="010-0000-0000"
                  onChange={(e) => handlePhoneNumber(e)}
                />
              </div>

              <div className="signup-password-field">
                <div className="signup-label">비밀번호</div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  placeholder="영문, 숫자 조합 8~15자"
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={validatePassword}
                  className={errors.password ? "input-error" : ""}
                />
                <img
                  src={showPassword ? "/seePwd.png" : "/hidePwd.png"}
                  alt={showPassword ? "Hide password" : "Show password"}
                  onClick={togglePasswordVisibility}
                  className="signup-password-toggle-icon"
                />
                {errors.password && (
                  <div className="signup-error-message">
                    비밀번호는 영문, 숫자를 포함한 8~15자리여야 합니다.
                  </div>
                )}
              </div>

              <div className="signup-confirmpwd-field">
                <div className="signup-label">비밀번호 확인</div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  value={confirmPassword}
                  placeholder="비밀번호를 한번 더 입력해주세요"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={
                    errors.confirmPassword || errors.confirmPasswordMismatch
                      ? "input-error"
                      : ""
                  }
                />
                <img
                  src={showConfirmPassword ? "/seePwd.png" : "/hidePwd.png"}
                  alt={showConfirmPassword ? "Hide password" : "Show password"}
                  onClick={toggleConfirmPasswordVisibility}
                  className="signup-password-toggle-icon"
                />
                {errors.confirmPasswordMismatch && (
                  <div className="signup-error-message">
                    비밀번호가 일치하지 않습니다.
                  </div>
                )}
              </div>
              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    termsAccepted ? "checked" : ""
                  }`}
                  onClick={() => setTermsAccepted(!termsAccepted)}
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/72c9e3515179406c891ffd983f931e3a?pvs=4" // 이용약관 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    이용약관
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <div className="terms-field">
                <div
                  className={`signup-custom-checkbox ${
                    privacyPolicyAccepted ? "checked" : ""
                  }`}
                  onClick={() =>
                    setPrivacyPolicyAccepted(!privacyPolicyAccepted)
                  }
                ></div>
                <div className="terms-field-label">
                  <a
                    href="https://koreaspacedata.notion.site/1cee08d37b584729a45645b6fa71138c?pvs=4" // 개인정보 처리방침 노션 페이지 URL
                    target="_blank"
                    rel="noopener noreferrer"
                    className="highlight-text"
                  >
                    개인정보 수집 및 이용
                  </a>
                  에 동의합니다 (필수)
                </div>
              </div>

              <button
                className="signup-submit-button"
                type="submit"
                onClick={handleSignup}
              >
                회원가입
              </button>
            </>
          )}
        </form>

        <div className="login-link">
          <div className="question-label">이미 회원이신가요?</div>
          <Link to="/login" className="to-login-button">
            로그인
          </Link>
        </div>
      </div>
      <footer className="footer">
        <img src="/ksdlogo.png" alt="ksd logo" className="footer-logo" />
        <div className="footer-text">
          Copyright Korea Space Data Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Signup;
